import * as React from 'react';

import { LoadStatus, Unit, enumUnit } from '@rematter/frontend-lib-api-client';
import { MeasuredValue, Money } from '@rematter/ui-web';

import {
  EstimatedPriceFieldCell,
  MaterialCell,
  QuantityOwedFieldCell,
  RequestedQuantityFieldCell,
  UnitPriceFieldCell,
} from './fields';
import { LineItemErrors } from './types';

import { useLoadLineItemsTotals } from '~/modules/trade/hooks/useLoadLineItemsTotals';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormLineItem, WeightUnitType } from '~/modules/trade/types/load';
import { loadHasBeenShipped } from '~/modules/trade/utils/load';
import {
  FooterCell,
  HeaderCell,
  LineItemFooter,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
} from '~/shared/components/line-items';

const GRID_TEMPLATE = 'grid-cols-[minmax(16rem,2fr)_120px_1fr_1fr_1fr]';

export interface SalesOrderLoadReadOnlyTableProps {
  lineItems: LoadFormLineItem[];
  correspondingLineItemsByType?: Map<
    string,
    { raw?: LoadFormLineItem; tagged: LoadFormLineItem[] }
  >;
  isLoading?: boolean;
  status?: LoadStatus;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  errors?: Record<string, LineItemErrors>;
}

export const SalesOrderLoadReadOnlyTable: React.FC<
  SalesOrderLoadReadOnlyTableProps
> = ({
  lineItems,
  isLoading = false,
  status,
  weightUnit = 'price',
  facilityDefaultUnit,
  errors,
}) => {
  const t = useTranslations();
  const isShipped = loadHasBeenShipped(status);

  const totals = useLoadLineItemsTotals({
    lineItems,
    facilityDefaultUnit: facilityDefaultUnit ?? enumUnit.Pounds,
    weightUnit,
  });

  const isEmpty = lineItems.length === 0;

  // Disabled update - Read only
  const updateField = () => null;

  return (
    <LineItemTable
      isEmpty={isEmpty}
      hideAddButton
      isLoading={isLoading}
      className="@SalesOrderLoadReadOnlyTable"
    >
      <LineItemHeader gridTemplate={GRID_TEMPLATE}>
        <HeaderCell className="justify-start">
          {t.itemTable.salesOrderLoadHeader.material}
        </HeaderCell>
        <HeaderCell>{t.itemTable.salesOrderLoadHeader.unitPrice}</HeaderCell>
        <HeaderCell>
          {isShipped
            ? t.itemTable.salesOrderLoadHeader.netWeight
            : `${t.itemTable.salesOrderLoadHeader.soRemaining} ${t.itemTable.salesOrderLoadHeader.weight}`}
        </HeaderCell>
        <HeaderCell>
          {t.itemTable.salesOrderLoadHeader.requestedWeight}
        </HeaderCell>
        <HeaderCell>
          {t.itemTable.salesOrderLoadHeader.estimatedPrice}
        </HeaderCell>
      </LineItemHeader>

      {lineItems.map((lineItem) => (
        <LineItemRow key={lineItem.id} gridTemplate={GRID_TEMPLATE}>
          <MaterialCell
            lineItem={lineItem}
            onUpdateLineItem={updateField}
            disableMaterialSelection
            errors={errors?.[lineItem.id]}
          />
          <UnitPriceFieldCell
            lineItem={lineItem}
            onUpdateLineItem={updateField}
            disabled
          />
          <QuantityOwedFieldCell
            lineItem={lineItem}
            weightUnit={weightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
            disabled
          />
          <RequestedQuantityFieldCell
            lineItem={lineItem}
            onUpdateLineItem={updateField}
            onChangeRequestedQuantity={updateField}
            disabled
            weightUnit={weightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
          />
          <EstimatedPriceFieldCell lineItem={lineItem} disabled />
        </LineItemRow>
      ))}

      {!isEmpty && (
        <LineItemFooter gridTemplate={GRID_TEMPLATE}>
          <FooterCell className="font-bold text-neutral">
            {totals.itemsCount}{' '}
            {t.itemTable.salesOrderLoadFooter.material(totals.itemsCount)}
          </FooterCell>
          <FooterCell />
          <FooterCell />
          <FooterCell className="flex-col items-end gap-1 font-bold">
            {!!totals.requestedWeight && (
              <div>
                <MeasuredValue
                  value={totals.requestedWeight.value}
                  unit={totals.requestedWeight.unit}
                />
              </div>
            )}
            {!!totals.requestedEachCount && (
              <div>
                <MeasuredValue
                  value={totals.requestedEachCount}
                  unit={enumUnit.Each}
                />
              </div>
            )}
          </FooterCell>
          <FooterCell className="justify-between font-bold">
            <Money amount={totals.totalEstimatedPrice} />
          </FooterCell>
        </LineItemFooter>
      )}
    </LineItemTable>
  );
};
